import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const Demos = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [Demos, setDemos] = useState([]);
  const [filteredDemos, setFilteredDemos] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Fetch Demos...
  useEffect(() => {
    const fetchDemos = async () => {
      try {
        const collectionRef = collection(fireStore, "Demos");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );
        const querySnapshot = await getDocs(q);

        const demosList = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });

        const sortedDemosList = demosList.sort((a, b) => b.id - a.id);
        setDemos(sortedDemosList);
        setFilteredDemos(sortedDemosList);
      } catch (error) {
        console.error("Error fetching Demos:", error);
      }
    };

    fetchDemos();
  }, [startDate, endDate]);

  // Fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Export...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    const container = document.getElementById("DemosContainer");
    const originalDisplay = container.style.display;
    container.style.display = "block";
    await new Promise((r) => setTimeout(r, 50)); // let DOM render

    const table = container.querySelector("table");
    if (table) {
      // lower scale + JPEG for smaller size
      const canvas = await html2canvas(table, { scale: 1 });
      const imgData = canvas.toDataURL("image/jpeg", 0.6);

      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let y = margin,
        remainingHeight = imgHeight,
        startY = 0;

      while (remainingHeight > 0) {
        if (startY > 0) {
          doc.addPage();
          y = margin;
        }

        const sliceHeight = Math.min(pageHeight - 2 * margin, remainingHeight);

        doc.addImage(
          imgData,
          "JPEG",
          margin,
          y,
          imgWidth,
          sliceHeight,
          undefined,
          "FAST" // fastest render
        );

        remainingHeight -= sliceHeight;
        startY += sliceHeight;
      }
    }

    doc.save("Demos.pdf");
    container.style.display = originalDisplay;
  };

  // Filters
  // Filter Logbooks...
  useEffect(() => {
    const filtered = Demos.filter(
      (book) =>
        book.user.territory.name.toLowerCase() ===
        selectedTerritory.toLowerCase()
    );
    setFilteredDemos(filtered);
    if (selectedTerritory === "All") {
      setFilteredDemos(Demos);
    }
  }, [selectedTerritory]);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = Demos.filter((visit) =>
      visit.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredDemos(filtered);
    if (searchQuery === "") {
      setFilteredDemos(Demos);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">Demos</h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center ">
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center ">
            <label
              htmlFor="to-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              id="to-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>
      {filteredDemos.map((demo) => (
        <div
          id={`Demo-${demo.id}`}
          key={demo.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-around">
                  <th className="py-2 px-4 border-b border-gray-200 text-left ">
                    {demo.user?.name}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left ">
                    {demo.user?.phone}
                  </th>

                  <th className="py-2 px-4 text-left">
                    {new Date(Number(demo.id)).toLocaleDateString("en-GB")}{" "}
                    {new Date(Number(demo.id)).toLocaleTimeString("en-GB", {
                      hour12: true,
                    })}{" "}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {/*<button className="border border-darkteal text-darkteal rounded-lg px-4 py-1">PDF*/}
                    {/*</button>*/}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Farmer Image</span>
                        <img
                          src={demo.farmer?.picture}
                          alt={demo.farmer?.name}
                          className="w-16 h-16 object-cover rounded-full"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Farmer Name</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.name}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Father Name</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.fatherName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Mobile Number</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.phone}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Cast</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.cast}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-7">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Acres</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.acres}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Address</span>
                        <span className=" font-lato font-bold">
                          {demo.farmer?.address}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Product</span>
                        <span className=" font-lato font-bold">
                          {demo.product}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Crop</span>
                        <span className=" font-lato font-bold">
                          {demo.crop}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-lato font-bold">Remarks</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          {demo.remarks}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div id="DemosContainer" class="bg-gray-100 hidden">
        <div class="container mx-auto py-8">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-darkteal">
              <thead>
                <tr>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Sr. No.
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Farmer Name
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Farmer Phone
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Product
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Crop
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDemos.map((item, index) => (
                  <tr key={item.id}>
                    <td class="px-4 py-2 border border-darkteal text-center">
                      {index + 1}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.farmer?.name}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.farmer?.phone}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.product}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.crop}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.remarks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demos;
